import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useTenant } from "@/contexts/TenantContext";

const STATUS_STYLE = {
  Active: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  Standby: "bg-slate-500/10 text-slate-400 border-slate-500/30",
  DOWN: "bg-red-500/15 text-red-300 border-red-500/40",
  "ACTIVE - ROUTING TRAFFIC": "bg-emerald-500/20 text-emerald-200 border-emerald-400 evos-pulse",
};

const ROLE_COLOR = {
  Primary: "#10B981",
  "Backup 1": "#F59E0B",
  "Backup 2": "#3B82F6",
  Tertiary: "#94A3B8",
};

const PROTO_COLOR = {
  IPsec: "#3B82F6",
  VXLAN: "#A855F7",
};

export default function TunnelsList({ refreshKey = 0 }) {
  const { tenant } = useTenant();
  const [tunnels, setTunnels] = useState([]);
  const [filter, setFilter] = useState("all"); // all | active

  useEffect(() => {
    if (!tenant) return;
    api.get(`/sdwan/tunnels?tenant=${tenant.id}`).then(({ data }) => setTunnels(data));
  }, [tenant, refreshKey]);

  const list = filter === "active" ? tunnels.filter((t) => t.status === "Active") : tunnels;
  const counts = {
    total: tunnels.length,
    active: tunnels.filter((t) => t.status === "Active").length,
  };

  return (
    <div data-testid="tunnels-list">
      <div className="px-5 py-3 border-b border-slate-800 flex items-center gap-3">
        <button
          data-testid="tunnels-filter-all"
          onClick={() => setFilter("all")}
          className={`text-[10px] font-mono uppercase tracking-wider px-2.5 py-1 rounded-sm border ${
            filter === "all" ? "border-[#3B82F6] bg-[#1E3A8A]/30 text-white" : "border-slate-800 text-slate-400 hover:border-slate-700"
          }`}
        >
          All · {counts.total}
        </button>
        <button
          data-testid="tunnels-filter-active"
          onClick={() => setFilter("active")}
          className={`text-[10px] font-mono uppercase tracking-wider px-2.5 py-1 rounded-sm border ${
            filter === "active" ? "border-emerald-500 bg-emerald-500/10 text-emerald-300" : "border-slate-800 text-slate-400 hover:border-slate-700"
          }`}
        >
          Active · {counts.active}
        </button>
        <div className="ml-auto text-[10px] font-mono text-slate-500">
          Protocols: <span className="text-[#60A5FA]">IPsec</span> · <span className="text-[#C084FC]">VXLAN</span>
        </div>
      </div>

      <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-[#0F172A]">
            <tr className="text-[10px] uppercase font-mono text-slate-500 border-b border-slate-800">
              <th className="text-left px-5 py-3">Status</th>
              <th className="text-left px-5 py-3">Source</th>
              <th className="text-left px-5 py-3">Destination</th>
              <th className="text-left px-5 py-3">Carrier · Role</th>
              <th className="text-center px-5 py-3">Protocol</th>
              <th className="text-right px-5 py-3 pr-6">Throughput</th>
            </tr>
          </thead>
          <tbody>
            {list.map((t) => {
              const stStyle = STATUS_STYLE[t.status] || STATUS_STYLE.Standby;
              return (
                <tr key={t.id} className="border-b border-slate-800/40 hover:bg-[#1E293B]/40" data-testid={`tunnel-row-${t.id}`}>
                  <td className="px-5 py-2.5">
                    <span className={`text-[10px] font-mono font-bold px-2 py-0.5 border ${stStyle}`}>
                      ● {t.status.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-5 py-2.5 text-slate-300 text-xs font-mono">{t.source}</td>
                  <td className="px-5 py-2.5">
                    <div className="text-slate-100 text-xs font-semibold">{t.destination}</div>
                    <div className="text-[9px] font-mono text-slate-500">{t.destination_id}</div>
                  </td>
                  <td className="px-5 py-2.5">
                    <div className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: ROLE_COLOR[t.role] || "#64748B" }} />
                      <div>
                        <div className="text-xs text-slate-200 font-semibold">{t.carrier}</div>
                        <div className="text-[9px] font-mono text-slate-500 uppercase">{t.role}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-2.5 text-center">
                    <span
                      className="text-[10px] font-mono font-bold px-2 py-0.5 border rounded-sm"
                      style={{ color: PROTO_COLOR[t.protocol], borderColor: PROTO_COLOR[t.protocol] + "55", background: PROTO_COLOR[t.protocol] + "12" }}
                    >
                      {t.protocol}
                    </span>
                  </td>
                  <td className="px-5 py-2.5 text-right pr-6 font-mono text-xs">
                    {t.status === "Active" || t.status === "ACTIVE - ROUTING TRAFFIC" ? (
                      <span className="text-slate-200">{t.throughput_mbps} <span className="text-slate-500">Mbps</span></span>
                    ) : (
                      <span className="text-slate-600">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
            {!list.length && (
              <tr><td colSpan={6} className="px-5 py-10 text-center text-xs text-slate-500 font-mono">No tunnels</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
